import { getFileBaseName, type ImageFormat } from "./imageFormats";
import type { ResultImage, SourceImage } from "./imageTypes";
import type { QualitySettings } from "./qualitySettings";

const DEFAULT_CONCURRENCY = Math.max(
  2,
  Math.min(6, (typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 4 : 4))
);

type ImageEncoder = (
  format: string,
  blob: Blob,
  compress: boolean,
  qualitySettings?: QualitySettings,
  signal?: AbortSignal
) => Promise<{ buffer: ArrayBuffer; originalSize: number; previewBuffer?: ArrayBuffer; error?: string }>;

const encodeWithWorker: ImageEncoder = async (format, blob, compress, qualitySettings, signal) => {
  const { getEncoderPool } = await import("./workerClients");
  return getEncoderPool().encode(format, blob, compress, qualitySettings, signal);
};

export const dataURItoBlob = (dataURI: string): Blob => {
  const byteString = atob(dataURI.split(",")[1]);
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
  const buffer = new ArrayBuffer(byteString.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
  return new Blob([buffer], { type: mimeString });
};

export const runTasksWithLimit = async <T,>(tasks: Array<() => Promise<T>>, limit: number): Promise<T[]> => {
  const results: T[] = [];
  let nextIndex = 0;
  const runnerCount = Math.max(1, Math.min(Number.isFinite(limit) ? Math.floor(limit) : DEFAULT_CONCURRENCY, tasks.length));

  const runNext = async (): Promise<void> => {
    const currentIndex = nextIndex++;
    if (currentIndex >= tasks.length) return;
    results[currentIndex] = await tasks[currentIndex]();
    await runNext();
  };

  const runners = Array.from({ length: runnerCount }, () => runNext());
  await Promise.all(runners);
  return results;
};

export const convertImageSource = async (
  source: SourceImage,
  format: ImageFormat,
  shouldCompress: boolean,
  qualitySettings?: QualitySettings,
  signal?: AbortSignal
): Promise<ResultImage | null> => {
  let previewUrl: string | undefined;
  try {
    const baseName = getFileBaseName(source.name);
    const outputName = `${baseName}.${format.ext}`;
    const result = await encodeWithWorker(
      format.mime,
      source.blob,
      shouldCompress,
      qualitySettings,
      signal
    );
    if (result.previewBuffer) {
      previewUrl = URL.createObjectURL(new Blob([result.previewBuffer], { type: "image/jpeg" }));
    }
    const blob = new Blob([result.buffer], { type: format.mime });
    const url = URL.createObjectURL(blob);

    return {
      blob,
      url,
      previewUrl,
      name: outputName,
      size: blob.size,
      originalSize: result.originalSize,
    };
  } catch (error) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    console.error("Image conversion failed:", error);
    return null;
  }
};

interface ProcessImageBatchOptions {
  sources: SourceImage[];
  formats: ImageFormat[];
  shouldCompress: boolean;
  qualitySettings?: QualitySettings;
  concurrency?: number;
  jobId?: number;
  isJobActive?: (jobId: number) => boolean;
  isFormatActive?: (format: ImageFormat) => boolean;
  getSignal?: (format: ImageFormat) => AbortSignal | undefined;
  convert?: typeof convertImageSource;
  onResult?: (format: ImageFormat, result: ResultImage) => void;
  onFormatProgress?: (format: ImageFormat, progress: FormatProgress) => void;
  onFormatError?: (format: ImageFormat, message: string) => void;
  onFormatDone?: (format: ImageFormat) => void;
}

export interface FormatProgress {
  completed: number;
  total: number;
  failed: number;
}

const isActive = (jobId: number | undefined, isJobActive?: (jobId: number) => boolean) =>
  jobId === undefined || !isJobActive || isJobActive(jobId);

const isTaskActive = (
  format: ImageFormat,
  jobId: number | undefined,
  isJobActive?: (jobId: number) => boolean,
  isFormatActive?: (format: ImageFormat) => boolean
) => isActive(jobId, isJobActive) && (!isFormatActive || isFormatActive(format));

const FORMAT_PRIORITY: Record<string, number> = {
  "image/jpeg": 0,
  "image/png": 1,
  "image/webp": 2,
  "image/bmp": 3,
  "application/pdf": 4,
  "image/x-icon": 5,
  "image/gif": 6,
  "image/tiff": 7,
  "image/avif": 8,
};

const getFormatPriority = (format: ImageFormat) => FORMAT_PRIORITY[format.mime] ?? 9;

const revokeResult = (result: ResultImage) => {
  URL.revokeObjectURL(result.url);
  if (result.previewUrl) {
    URL.revokeObjectURL(result.previewUrl);
  }
};

export const processImageBatch = async ({
  sources,
  formats,
  shouldCompress,
  qualitySettings,
  concurrency = DEFAULT_CONCURRENCY,
  jobId,
  isJobActive,
  isFormatActive,
  getSignal,
  convert = convertImageSource,
  onResult,
  onFormatProgress,
  onFormatError,
  onFormatDone,
}: ProcessImageBatchOptions) => {
  const progressByMime = new Map<string, FormatProgress>();
  formats.forEach((format) => {
    progressByMime.set(format.mime, {
      completed: 0,
      total: sources.length,
      failed: 0,
    });
  });

  const recordProgress = (format: ImageFormat, failed: boolean, message?: string) => {
    const progress = progressByMime.get(format.mime);
    if (!progress) return;
    const nextProgress = {
      completed: progress.completed + 1,
      total: progress.total,
      failed: progress.failed + (failed ? 1 : 0),
    };
    progressByMime.set(format.mime, nextProgress);
    if (failed && message) {
      onFormatError?.(format, message);
    }
    onFormatProgress?.(format, nextProgress);
    if (nextProgress.completed >= nextProgress.total) {
      onFormatDone?.(format);
    }
  };

  const prioritizedFormats = [...formats].sort((a, b) => getFormatPriority(a) - getFormatPriority(b));

  const tasks = prioritizedFormats.flatMap((format) =>
    sources.map((source) => async () => {
      if (!isTaskActive(format, jobId, isJobActive, isFormatActive)) return;

      let result: ResultImage | null = null;
      try {
        result = await convert(
          source,
          format,
          shouldCompress,
          qualitySettings,
          getSignal?.(format)
        );
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        if (isTaskActive(format, jobId, isJobActive, isFormatActive)) {
          recordProgress(
            format,
            true,
            err instanceof Error ? err.message : "Conversion failed"
          );
        }
        return;
      }

      if (!isTaskActive(format, jobId, isJobActive, isFormatActive)) {
        if (result) {
          revokeResult(result);
        }
        return;
      }

      if (result) {
        onResult?.(format, result);
        recordProgress(format, false);
      } else {
        recordProgress(format, true, "Conversion failed");
      }
    })
  );

  await runTasksWithLimit(tasks, concurrency);
};
